import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Slide,
  CircularProgress,
  Snackbar,
  Alert,
  Skeleton,
  Paper,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  Save as SaveIcon,
  CardMembership as MembershipIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import MembershipDetails from "./MembershipDetails";
import CreateInstituteModal from "./CreateInstituteModal";
import "antd/dist/reset.css";

dayjs.extend(customParseFormat);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const instituteTypes = ["Engineering", "Degree", "University", "Diploma"];

const theme = createTheme({
  typography: {
    fontFamily:
      '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2E7CF6",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F8F9FB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1F1F1F",
      secondary: "#6B7280",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            fontSize: "13px",
            "& fieldset": {
              borderColor: "#D1D5DB",
              borderRadius: "4px",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#3B82F6",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "6px 10px",
            height: "auto",
            color: "#1F1F1F",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "6px 14px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
  },
});

const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Box
    sx={{
      minWidth: width,
      maxWidth: width,
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 400,
      color: "#4B5563",
      pr: 2,
    }}
  >
    {children}
    {required && <span style={{ color: "#E85858", marginLeft: "2px" }}>*</span>}
  </Box>
);

const FormField = ({ children, sx = {} }) => (
  <Box
    sx={{
      mb: 1.5,
      display: "flex",
      alignItems: "flex-start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const ViewField = ({ label, value, isLink = false }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: "11px",
        color: "#6B7280",
        mb: 0.5,
        textTransform: "uppercase",
        fontWeight: 600,
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "13px",
        color: isLink ? "#3B82F6" : "#1F1F1F",
        fontWeight: isLink ? 500 : 400,
      }}
    >
      {isLink && value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "#3B82F6" }}
        >
          {value}
        </a>
      ) : (
        value || "-"
      )}
    </Typography>
  </Box>
);

const CreateMembershipForm = () => {
  const [institutes, setInstitutes] = useState([]);
  const [institutesPage, setInstitutesPage] = useState(1);
  const [institutesTotalCount, setInstitutesTotalCount] = useState(0);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [hasMoreInstitutes, setHasMoreInstitutes] = useState(true);
  const [instituteSearchTerm, setInstituteSearchTerm] = useState("");
  const listboxRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [loadingInstituteDetails, setLoadingInstituteDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Institute form states
  const [instituteName, setInstituteName] = useState("");
  const [instituteType, setInstituteType] = useState(null);
  const [universityName, setUniversityName] = useState("");
  const [currentInstituteTypes, setCurrentInstituteTypes] =
    useState(instituteTypes);
  const [billingStates, setBillingStates] = useState([]);
  const [billingCities, setBillingCities] = useState([]);
  const [billingAttention, setBillingAttention] = useState("");
  const [billingCountry, setBillingCountry] = useState(null);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingCity, setBillingCity] = useState(null);
  const [billingState, setBillingState] = useState(null);
  const [billingPincode, setBillingPincode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingFax, setBillingFax] = useState("");
  const [website, setWebsite] = useState("");
  const [gstinNo, setGstinNo] = useState("");

  // Create Institute Modal states
  const [openInstituteModal, setOpenInstituteModal] = useState(false);

  // Other states
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [newInstituteType, setNewInstituteType] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    fetchInstitutes(1, "");
  }, []);

  const fetchInstitutes = async (page, search) => {
    if (loadingInstitutes) return;

    setLoadingInstitutes(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/get-all-institutes`,
        {
          params: {
            page: page,
            pagesize: 20,
            search: search,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const newInstitutes = response.data.institutes;
      const total = response.data.total || 0;

      if (page === 1) {
        setInstitutes(newInstitutes);
      } else {
        setInstitutes((prev) => [...prev, ...newInstitutes]);
      }

      setInstitutesTotalCount(total);
      setHasMoreInstitutes(institutes.length + newInstitutes.length < total);
      setInstitutesPage(page);
    } catch (error) {
      console.error("Error fetching institutes:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch institutes",
        severity: "error",
      });
    } finally {
      setLoadingInstitutes(false);
    }
  };

  const handleScroll = useCallback(
    (event) => {
      const listboxNode = event.currentTarget;
      const position = listboxNode.scrollTop + listboxNode.clientHeight;
      if (listboxNode.scrollHeight - position <= 1) {
        if (hasMoreInstitutes && !loadingInstitutes) {
          fetchInstitutes(institutesPage + 1, instituteSearchTerm);
        }
      }
    },
    [hasMoreInstitutes, loadingInstitutes, institutesPage, instituteSearchTerm]
  );

  const handleInstituteSearch = (event, value, reason) => {
    if (reason === "input") {
      setInstituteSearchTerm(value);
      setInstitutesPage(1);
      setInstitutes([]);
      setHasMoreInstitutes(true);

      const timeoutId = setTimeout(() => {
        fetchInstitutes(1, value);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  };

  const fetchInstituteDetails = async (instituteId) => {
    setLoadingInstituteDetails(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/institute/${instituteId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const data = response.data;
      populateInstituteData(data);
    } catch (error) {
      console.error("Error fetching institute details:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch institute details",
        severity: "error",
      });
    } finally {
      setLoadingInstituteDetails(false);
    }
  };

  const populateInstituteData = (data) => {
    setInstituteName(data.institute_name || "");
    setInstituteType(data.institute_type || null);
    setUniversityName(data.university_name || "");
    setWebsite(data.website || "");
    setGstinNo(data.gstin_no || "");

    if (data.billing_address) {
      const addr = data.billing_address;
      setBillingAttention(addr.attention || "");
      setBillingAddressLine1(addr.address_line1 || "");
      setBillingAddressLine2(addr.address_line2 || "");
      setBillingPincode(addr.pincode || "");

      const country = countries.find((c) => c.name === addr.country_name);
      if (country) {
        setBillingCountry(country);

        setTimeout(() => {
          if (addr.state_name) {
            const states = State.getStatesOfCountry(country.isoCode);
            const state = states.find((s) => s.name === addr.state_name);
            if (state) {
              setBillingState(state);

              setTimeout(() => {
                if (addr.city) {
                  const cities = City.getCitiesOfState(
                    country.isoCode,
                    state.isoCode
                  );
                  const city = cities.find((c) => c.name === addr.city);
                  if (city) {
                    setBillingCity(city);
                  } else {
                    setBillingCity(addr.city);
                  }
                }
              }, 100);
            }
          }
        }, 100);
      }

      if (addr.phone) {
        const phoneWithoutCode = addr.phone.replace(/.*-/, "");
        setBillingPhone(phoneWithoutCode);
      }

      if (addr.fax) {
        const faxWithoutCode = addr.fax.replace(/.*-/, "");
        setBillingFax(faxWithoutCode);
      }
    }
  };

  // Function to clear all institute data
  const clearInstituteData = () => {
    setInstituteName("");
    setInstituteType(null);
    setUniversityName("");
    setWebsite("");
    setGstinNo("");
    setBillingAttention("");
    setBillingAddressLine1("");
    setBillingAddressLine2("");
    setBillingPincode("");
    setBillingCountry(null);
    setBillingState(null);
    setBillingCity(null);
    setBillingStates([]);
    setBillingCities([]);
    setBillingPhone("");
    setBillingFax("");
  };

  const updateInstitute = async () => {
    setAttemptedSubmit(true);

    // Validate all required fields
    if (!instituteName.trim()) {
      setSnackbar({
        open: true,
        message: "Institute name is required",
        severity: "error",
      });
      return;
    }

    if (
      !billingCountry ||
      !billingState ||
      !billingAddressLine1.trim() ||
      !billingCity ||
      !billingPincode.trim()
    ) {
      setSnackbar({
        open: true,
        message: "Please fill in all required address fields",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const phoneCode = billingCountry?.phonecode
        ? `+${billingCountry.phonecode}`
        : "";
      const fullPhone = billingPhone ? `${phoneCode}-${billingPhone}` : "";
      const fullFax = billingFax ? `${phoneCode}-${billingFax}` : "";

      const payload = {
        institute_name: instituteName,
        institute_type: instituteType || "",
        university_name: universityName || instituteName,
        billing_address: {
          attention: billingAttention,
          address_line1: billingAddressLine1,
          address_line2: billingAddressLine2,
          city:
            typeof billingCity === "string" ? billingCity : billingCity?.name,
          state_name: billingState?.name,
          country_name: billingCountry?.name,
          pincode: billingPincode,
          phone: fullPhone,
          fax: fullFax,
        },
        website: website,
        gstin_no: gstinNo,
      };

      await axios.put(
        `${BASE_URL}/accounts/institute/${selectedInstitute.institue_id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setSnackbar({
        open: true,
        message: "Institute details updated successfully!",
        severity: "success",
      });

      setIsEditing(false);
      await fetchInstituteDetails(selectedInstitute.institue_id);
    } catch (error) {
      console.error("Error updating institute:", error);
      setSnackbar({
        open: true,
        message: "Failed to update institute",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (billingCountry) {
      const states = State.getStatesOfCountry(billingCountry.isoCode);
      setBillingStates(states);
    } else {
      setBillingStates([]);
      setBillingState(null);
    }
  }, [billingCountry]);

  useEffect(() => {
    if (billingCountry && billingState) {
      const cities = City.getCitiesOfState(
        billingCountry.isoCode,
        billingState.isoCode
      );
      setBillingCities(cities);
    } else {
      setBillingCities([]);
    }
  }, [billingState, billingCountry]);

  const handleInstituteChange = (event, newValue) => {
    // Check if "Create New" option is selected
    if (newValue && newValue.institue_id === "CREATE_NEW") {
      setOpenInstituteModal(true);
      return;
    }

    if (!newValue) {
      setSelectedInstitute(null);
      // Clear all institute-related data
      clearInstituteData();
      return;
    }

    // Clear previous institute data immediately before fetching new data
    clearInstituteData();

    setSelectedInstitute(newValue);
    setIsEditing(false);
    fetchInstituteDetails(newValue.institue_id);
  };

  const handleInstituteModalSuccess = (newInstitute) => {
    setSnackbar({
      open: true,
      message: "Institute created successfully!",
      severity: "success",
    });

    // Add the newly created institute to the list
    setInstitutes((prev) => [newInstitute, ...prev]);
    setInstitutesTotalCount((prev) => prev + 1);

    // Set the newly created institute as selected
    if (newInstitute && newInstitute.institue_id) {
      setSelectedInstitute(newInstitute);
      fetchInstituteDetails(newInstitute.institue_id);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setAttemptedSubmit(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setAttemptedSubmit(false);
    if (selectedInstitute) {
      fetchInstituteDetails(selectedInstitute.institue_id);
    }
  };

  const handleCityChange = (event, newValue) => {
    if (!newValue) {
      setBillingCity(null);
      return;
    }
    if (typeof newValue === "string") {
      setBillingCity(newValue);
      return;
    }
    setBillingCity(newValue);
  };

  const handleOpenTypeDialog = () => {
    setOpenTypeDialog(true);
  };

  const handleCloseTypeDialog = () => {
    setOpenTypeDialog(false);
    setNewInstituteType("");
  };

  const handleCreateNewType = () => {
    if (newInstituteType.trim()) {
      setCurrentInstituteTypes([...currentInstituteTypes, newInstituteType]);
      setInstituteType(newInstituteType);
      handleCloseTypeDialog();
    }
  };

  const getPhoneCode = () => {
    if (billingCountry && billingCountry.phonecode) {
      return `+${billingCountry.phonecode}`;
    }
    return "";
  };

  const handleSubmit = () => {
    const formData = {
      institute: {
        id: selectedInstitute?.institue_id,
        name: selectedInstitute?.institute_name,
      },
    };

    console.log("Membership Data:", formData);
    alert("Membership created successfully! Check console for details.");
  };

  const handleReset = () => {
    setSelectedInstitute(null);
    setIsEditing(false);
    clearInstituteData();
  };

  const isFormValid = () => {
    return selectedInstitute !== null;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Function to format the billing address - Only City, State, Country, Pincode
  const formatBillingAddress = () => {
    const parts = [];

    const cityName =
      typeof billingCity === "string" ? billingCity : billingCity?.name;
    const cityStatePincode = [cityName, billingState?.name, billingPincode]
      .filter(Boolean)
      .join(", ");

    if (cityStatePincode) {
      parts.push(cityStatePincode);
    }

    if (billingCountry?.name) {
      parts.push(billingCountry.name);
    }

    return parts.length > 0 ? parts.join(", ") : "-";
  };

  return (
    <ThemeProvider theme={theme}>
      <style>
        {`
          .ant-picker {
            width: 100%;
            border-radius: 4px;
            border-color: #D1D5DB;
            font-size: 13px;
            font-family: "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif;
          }
          .ant-picker:hover {
            border-color: #9CA3AF;
          }
          .ant-picker-focused {
            border-color: #3B82F6 !important;
            box-shadow: none !important;
          }
        `}
      </style>

      <Box
        sx={{
          mx: "auto",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, pb: "80px" }}>
          <Box sx={{ p: 0, mb: 0, backgroundColor: "#FFFFFF" }}>
            <Box sx={{ p: 3, pb: 2, maxWidth: "900px" }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1F1F1F",
                  mb: 3,
                }}
              >
                Institute Details
              </Typography>

              <Box sx={{ maxWidth: "900px" }}>
                <FormField>
                  <FieldLabel required>Select Institute</FieldLabel>
                  <Autocomplete
                    value={selectedInstitute}
                    onChange={handleInstituteChange}
                    onInputChange={handleInstituteSearch}
                    options={institutes}
                    loading={loadingInstitutes}
                    getOptionLabel={(option) => option.institute_name || ""}
                    ListboxProps={{
                      onScroll: handleScroll,
                      ref: listboxRef,
                      style: { maxHeight: "300px", padding: 0 },
                    }}
                    sx={{
                      flex: 1,
                      maxWidth: "400px",
                    }}
                    ListboxComponent={React.forwardRef((props, ref) => {
                      const { children, ...other } = props;
                      return (
                        <ul {...other} ref={ref}>
                          {/* Sticky "Create New" option */}
                          <Box
                            component="li"
                            onClick={() => setOpenInstituteModal(true)}
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                              backgroundColor: "#FFFFFF",
                              color: "#3B82F6",
                              fontWeight: 500,
                              fontSize: "13px",
                              borderBottom: "1px solid #E5E7EB",
                              pb: 1,
                              mb: 1,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              "&:hover": {
                                backgroundColor: "#F3F4F6",
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            Create New Institute
                          </Box>
                          {/* Scrollable institute list */}
                          {children}
                        </ul>
                      );
                    })}
                    renderOption={(props, option) => {
                      return (
                        <Box {...props} component="li">
                          <Box>
                            <Typography
                              sx={{ fontSize: "13px", fontWeight: 500 }}
                            >
                              {option.institute_name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "11px", color: "#6B7280" }}
                            >
                              {option.institute_type} • {option.city_name},{" "}
                              {option.state_name}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select institute"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingInstitutes ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={
                      loadingInstitutes
                        ? "Loading..."
                        : instituteSearchTerm
                        ? "No institutes found"
                        : "Type to search institutes"
                    }
                  />
                </FormField>
              </Box>

              {loadingInstituteDetails ? (
                <Box sx={{ mt: 3 }}>
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={20}
                    sx={{ mb: 2 }}
                  />
                  <Skeleton variant="rectangular" height={100} />
                </Box>
              ) : null}

              {selectedInstitute && !loadingInstituteDetails ? (
                <>
                  {!isEditing ? (
                    // View Mode - Only Billing Address (City, State, Country, Pincode)
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1F1F1F",
                          }}
                        >
                          Billing Address
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={handleEdit}
                          sx={{
                            color: "#6B7280",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "#F3F4F6",
                              color: "#3B82F6",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#1F1F1F",
                          fontWeight: 400,
                          lineHeight: 1.8,
                        }}
                      >
                        {formatBillingAddress()}
                      </Typography>
                    </Box>
                  ) : (
                    // Edit Mode - All Fields
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        mt: 3,
                        mb: 2,
                        backgroundColor: "#F9FAFB",
                        border: "1px solid #E5E7EB",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#6B7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          mb: 3,
                        }}
                      >
                        Edit Institute Information
                      </Typography>

                      {/* Basic Information */}
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#1F1F1F",
                          mb: 2,
                        }}
                      >
                        Basic Information
                      </Typography>

                      <Box sx={{ maxWidth: "900px" }}>
                        <FormField>
                          <FieldLabel required>Institute Name</FieldLabel>
                          <TextField
                            value={instituteName}
                            onChange={(e) => setInstituteName(e.target.value)}
                            placeholder="Enter institute name"
                            size="small"
                            sx={{ flex: 1, maxWidth: "350px" }}
                            error={attemptedSubmit && !instituteName.trim()}
                            helperText={
                              attemptedSubmit && !instituteName.trim()
                                ? "Institute name is required"
                                : ""
                            }
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel>Institute Type</FieldLabel>
                          <Autocomplete
                            value={instituteType}
                            onChange={(event, newValue) => {
                              if (newValue === "Create New") {
                                handleOpenTypeDialog();
                              } else {
                                setInstituteType(newValue);
                              }
                            }}
                            options={[...currentInstituteTypes, "Create New"]}
                            sx={{ flex: 1, maxWidth: "350px" }}
                            renderOption={(props, option) => {
                              if (option === "Create New") {
                                return (
                                  <li
                                    {...props}
                                    style={{
                                      color: "#3B82F6",
                                      fontWeight: 500,
                                      fontSize: "13px",
                                    }}
                                  >
                                    <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    Create New
                                  </li>
                                );
                              }
                              return (
                                <li {...props} style={{ fontSize: "13px" }}>
                                  {option}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select institute type"
                                size="small"
                              />
                            )}
                          />
                        </FormField>

                        <Divider sx={{ my: 3 }} />

                        {/* Billing Address */}
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1F1F1F",
                            mb: 2,
                          }}
                        >
                          Billing Address
                        </Typography>

                        <FormField>
                          <FieldLabel>Attention</FieldLabel>
                          <TextField
                            value={billingAttention}
                            onChange={(e) =>
                              setBillingAttention(e.target.value)
                            }
                            size="small"
                            sx={{ flex: 1, maxWidth: "300px" }}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel required>Country/Region</FieldLabel>
                          <Autocomplete
                            value={billingCountry}
                            onChange={(event, newValue) => {
                              setBillingCountry(newValue);
                              if (!newValue) {
                                setBillingState(null);
                                setBillingCity(null);
                                setBillingPhone("");
                                setBillingFax("");
                              }
                            }}
                            options={countries}
                            getOptionLabel={(option) => option.name || ""}
                            renderOption={(props, option) => (
                              <li {...props} style={{ fontSize: "13px" }}>
                                {option.flag} {option.name}
                              </li>
                            )}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select country"
                                size="small"
                                error={attemptedSubmit && !billingCountry}
                                helperText={
                                  attemptedSubmit && !billingCountry
                                    ? "Country is required"
                                    : ""
                                }
                              />
                            )}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel required>State</FieldLabel>
                          <Autocomplete
                            value={billingState}
                            onChange={(event, newValue) => {
                              setBillingState(newValue);
                              if (!newValue) {
                                setBillingCity(null);
                              }
                            }}
                            options={billingStates}
                            getOptionLabel={(option) => option.name || ""}
                            disabled={!billingCountry}
                            renderOption={(props, option) => (
                              <li {...props} style={{ fontSize: "13px" }}>
                                {option.name}
                              </li>
                            )}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  billingCountry
                                    ? "Select state"
                                    : "Select country first"
                                }
                                size="small"
                                error={attemptedSubmit && !billingState}
                                helperText={
                                  attemptedSubmit && !billingState
                                    ? "State is required"
                                    : ""
                                }
                              />
                            )}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel required>City</FieldLabel>
                          <Autocomplete
                            freeSolo
                            value={billingCity}
                            onChange={handleCityChange}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "input") {
                                setBillingCity(newInputValue);
                              }
                            }}
                            options={billingCities}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              return option?.name || "";
                            }}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options.slice(0, 50);
                              return options
                                .filter((option) =>
                                  option.name
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase())
                                )
                                .slice(0, 50);
                            }}
                            disabled={!billingState}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  billingState
                                    ? "Enter or select city"
                                    : "Select state first"
                                }
                                size="small"
                                error={attemptedSubmit && !billingCity}
                                helperText={
                                  attemptedSubmit && !billingCity
                                    ? "City is required"
                                    : ""
                                }
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} style={{ fontSize: "13px" }}>
                                {option.name}
                              </li>
                            )}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel required>Address</FieldLabel>
                          <TextField
                            value={billingAddressLine1}
                            onChange={(e) =>
                              setBillingAddressLine1(e.target.value)
                            }
                            placeholder="Street 1"
                            multiline
                            rows={2}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            error={
                              attemptedSubmit && !billingAddressLine1.trim()
                            }
                            helperText={
                              attemptedSubmit && !billingAddressLine1.trim()
                                ? "Address is required"
                                : ""
                            }
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel width="140px"></FieldLabel>
                          <TextField
                            value={billingAddressLine2}
                            onChange={(e) =>
                              setBillingAddressLine2(e.target.value)
                            }
                            placeholder="Street 2"
                            multiline
                            rows={2}
                            sx={{ flex: 1, maxWidth: "300px" }}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel required>Pin Code</FieldLabel>
                          <TextField
                            value={billingPincode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (value.length <= 10) {
                                setBillingPincode(value);
                              }
                            }}
                            size="small"
                            inputProps={{ maxLength: 10 }}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            error={attemptedSubmit && !billingPincode.trim()}
                            helperText={
                              attemptedSubmit && !billingPincode.trim()
                                ? "Pin code is required"
                                : ""
                            }
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel>Phone</FieldLabel>
                          <TextField
                            value={billingPhone}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              setBillingPhone(value);
                            }}
                            placeholder={
                              billingCountry
                                ? "Enter phone number"
                                : "Select country first"
                            }
                            size="small"
                            disabled={!billingCountry}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            InputProps={{
                              startAdornment: billingCountry && (
                                <InputAdornment position="start">
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: "#6B7280",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {getPhoneCode()}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel>Fax Number</FieldLabel>
                          <TextField
                            value={billingFax}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              setBillingFax(value);
                            }}
                            placeholder={
                              billingCountry
                                ? "Enter fax number"
                                : "Select country first"
                            }
                            size="small"
                            disabled={!billingCountry}
                            sx={{ flex: 1, maxWidth: "300px" }}
                            InputProps={{
                              startAdornment: billingCountry && (
                                <InputAdornment position="start">
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: "#6B7280",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {getPhoneCode()}
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </FormField>

                        <Divider sx={{ my: 3 }} />

                        {/* Other Details */}
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1F1F1F",
                            mb: 2,
                          }}
                        >
                          Other Details
                        </Typography>

                        <FormField>
                          <FieldLabel>Institute Website</FieldLabel>
                          <TextField
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            type="url"
                            size="small"
                            placeholder="https://example.com"
                            sx={{ flex: 1, maxWidth: "300px" }}
                          />
                        </FormField>

                        <FormField>
                          <FieldLabel>GSTIN No.</FieldLabel>
                          <TextField
                            value={gstinNo}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              if (value.length <= 15) {
                                setGstinNo(value);
                              }
                            }}
                            size="small"
                            inputProps={{ maxLength: 15 }}
                            placeholder="Enter GSTIN number"
                            sx={{ flex: 1, maxWidth: "300px" }}
                          />
                        </FormField>

                        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={
                              isSubmitting ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            onClick={updateInstitute}
                            disabled={isSubmitting}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              backgroundColor: "#3B82F6",
                              "&:hover": { backgroundColor: "#2563EB" },
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              borderColor: "#D1D5DB",
                              color: "#6B7280",
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {/* Membership Details */}
                  {selectedInstitute && !loadingInstituteDetails && (
                    <MembershipDetails selectedInstitute={selectedInstitute} />
                  )}
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Create New Institute Type Dialog */}
      <Dialog
        open={openTypeDialog}
        onClose={handleCloseTypeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, px: 3, py: 2 }}>
          Create New Institute Type
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            value={newInstituteType}
            onChange={(e) => setNewInstituteType(e.target.value)}
            placeholder="Enter new institute type"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseTypeDialog}
            sx={{
              color: "#6B7280",
              textTransform: "none",
              fontSize: "13px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewType}
            variant="contained"
            disabled={!newInstituteType.trim()}
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": { backgroundColor: "#2563EB" },
              textTransform: "none",
              fontSize: "13px",
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Institute Modal */}
      <CreateInstituteModal
        open={openInstituteModal}
        onClose={() => setOpenInstituteModal(false)}
        onSuccess={handleInstituteModalSuccess}
        instituteTypes={currentInstituteTypes}
        onCreateNewType={handleOpenTypeDialog}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default CreateMembershipForm;
